import { useState, useEffect, useCallback, useRef } from "react";
import { Match } from "../types";
import { updateMatchStatus } from "../../../lib/loggerApi";
import { deriveClockState } from "../lib/clockStateMachine";

export const FIRST_HALF_MINUTES = 45;
export const SECOND_HALF_MINUTES = 90; // Total match time
export const EXTRA_HALF_MINUTES = 15;
export const REGULATION_FIRST_HALF_SECONDS = FIRST_HALF_MINUTES * 60;
export const REGULATION_SECOND_HALF_SECONDS = SECOND_HALF_MINUTES * 60;
export const EXTRA_FIRST_HALF_END_SECONDS =
  REGULATION_SECOND_HALF_SECONDS + EXTRA_HALF_MINUTES * 60; // 105:00
export const EXTRA_SECOND_HALF_END_SECONDS =
  EXTRA_FIRST_HALF_END_SECONDS + EXTRA_HALF_MINUTES * 60; // 120:00

export type PeriodPhase =
  | "NOT_STARTED"
  | "FIRST_HALF"
  | "FIRST_HALF_EXTRA_TIME"
  | "HALFTIME"
  | "EXTRA_HALFTIME"
  | "SECOND_HALF"
  | "SECOND_HALF_EXTRA_TIME"
  | "PENALTIES"
  | "FULLTIME"
  | "COMPLETED";

interface PeriodInfo {
  period: number;
  phase: PeriodPhase;
  isExtraTime: boolean;
  extraTimeSeconds: number;
  shouldShowExtraTimeWarning: boolean;
  canTransitionToHalftime: boolean;
  canTransitionToSecondHalf: boolean;
  canTransitionToFulltime: boolean;
  canTransitionToExtraTimeFirst: boolean;
  canTransitionToExtraHalftime: boolean;
  canTransitionToExtraTimeSecond: boolean;
  canTransitionToPenalties: boolean;
  canFinishMatch: boolean;
  /** Seconds elapsed since the current period started (resets to 0 each period) */
  periodElapsedSeconds: number;
  /** Minimum regulation seconds for the current period (2700 for regular halves, 900 for extra time halves) */
  periodMinimumSeconds: number;
}

// Map match status to phase
export const statusToPhase = (
  status: string | undefined,
): PeriodPhase | null => {
  switch (status) {
    case "Scheduled":
    case "Pending":
      return "NOT_STARTED";
    case "Live":
    case "Live_First_Half":
      return "FIRST_HALF";
    case "Halftime":
      return "HALFTIME";
    case "Live_Second_Half":
      return "SECOND_HALF";
    case "Live_Extra_First":
      return "FIRST_HALF_EXTRA_TIME";
    case "Extra_Halftime":
      return "EXTRA_HALFTIME";
    case "Live_Extra_Second":
      return "SECOND_HALF_EXTRA_TIME";
    case "Penalties":
      return "PENALTIES";
    case "Fulltime":
      return "FULLTIME";
    case "Completed":
      return "COMPLETED";
    default:
      return null;
  }
};

// Canonical start-of-period baseline seconds, keyed by phase. Also reused by
// `computeBroadcastClock` (`lib/clockHelpers.ts`) for the broadcast-cap
// display and by the public share view (no `usePeriodManager` there).
export const CANONICAL_START_SECONDS: Partial<Record<PeriodPhase, number>> = {
  FIRST_HALF: 0,
  SECOND_HALF: REGULATION_FIRST_HALF_SECONDS,
  FIRST_HALF_EXTRA_TIME: REGULATION_SECOND_HALF_SECONDS,
  SECOND_HALF_EXTRA_TIME: EXTRA_FIRST_HALF_END_SECONDS,
};

const PERIOD_KEY_BY_PHASE: Partial<Record<PeriodPhase, string>> = {
  FIRST_HALF: "1",
  SECOND_HALF: "2",
  FIRST_HALF_EXTRA_TIME: "3",
  SECOND_HALF_EXTRA_TIME: "4",
};

// PMA-COCK-210 (EPIC-61): the match's fixed, non-branching phase sequence,
// as a total order. Module-level export (was a local const re-declared
// inside `usePeriodManager` below, byte-identical values) so
// `MatchPeriodSelector.tsx`'s phase ladder (EPIC-61 brief 3) can classify
// each of its rows as completed/live/future by rank comparison alone,
// without duplicating this table a second time and risking drift between
// the two. FULLTIME and EXTRA_HALFTIME are real phases the match sits in
// (a genuine resting/fork state, `_is_transition_allowed`-legal statuses on
// the backend) even though the ladder draws no dedicated row for either —
// see that file's own comment for how a "gap" phase's reachable next rows
// become actionable instead.
export const PHASE_RANK: Record<PeriodPhase, number> = {
  NOT_STARTED: 0,
  FIRST_HALF: 1,
  HALFTIME: 2,
  SECOND_HALF: 3,
  FULLTIME: 4, // End of Regulation
  FIRST_HALF_EXTRA_TIME: 5,
  EXTRA_HALFTIME: 6,
  SECOND_HALF_EXTRA_TIME: 7,
  PENALTIES: 8,
  COMPLETED: 9,
};

/**
 * Resolves a period's start-of-period baseline in global-clock seconds.
 * Absent/invalid (non-finite or negative) `rawStartSeconds` falls back to
 * `canonicalStartSeconds`. A present-but-inflated raw value (e.g. a
 * double-counted persisted `global_start_seconds` that exceeds the current
 * global clock) is clamped to `globalTimeSeconds` rather than discarded, so
 * the period renders exactly at its regulation start (e.g. 45:00) instead
 * of silently reverting to the canonical baseline and losing any legitimate
 * stoppage-time carryover already reflected in the global clock.
 */
export const resolvePeriodStartSeconds = (
  rawStartSeconds: number | null | undefined,
  canonicalStartSeconds: number,
  globalTimeSeconds: number,
): number => {
  if (
    typeof rawStartSeconds !== "number" ||
    !Number.isFinite(rawStartSeconds) ||
    rawStartSeconds < 0
  ) {
    return canonicalStartSeconds;
  }
  return Math.min(rawStartSeconds, globalTimeSeconds);
};

/**
 * Seconds elapsed since the given phase's period started (resets to 0 each
 * period). Prefers the persisted `period_timestamps[key].global_start_seconds`
 * when present and valid, else falls back to the canonical baseline for
 * `phase`.
 */
export const computePeriodElapsedSeconds = (
  phase: PeriodPhase,
  globalTimeSeconds: number,
  periodTimestamps?: Match["period_timestamps"],
): number => {
  const canonicalStartSeconds =
    CANONICAL_START_SECONDS[phase] ?? globalTimeSeconds;
  const periodKey = PERIOD_KEY_BY_PHASE[phase] ?? null;
  const rawStartSeconds = periodKey
    ? periodTimestamps?.[periodKey]?.global_start_seconds
    : null;
  const startSeconds = resolvePeriodStartSeconds(
    rawStartSeconds,
    canonicalStartSeconds,
    globalTimeSeconds,
  );
  return Math.max(0, globalTimeSeconds - startSeconds);
};

export const usePeriodManager = (
  match: Match | null,
  effectiveTime: number,
  globalTimeSeconds: number,
  clockMode: "EFFECTIVE" | "INEFFECTIVE",
  isClockRunning: boolean,
  handleModeSwitch: (mode: "EFFECTIVE" | "INEFFECTIVE") => void,
  fetchMatch: () => void,
  onTransitionError?: (info: {
    target: Match["status"];
    error: unknown;
  }) => void,
  /**
   * PMA-COCK-144 extension: the broadcast-/breakdown-derived scoped-stoppage
   * flags (same values CockpitContext.tsx feeds useCockpitClockHandlers'
   * clock-lifecycle guard) — needed so a period transition that resumes
   * EFFECTIVE (second half, either extra-time half) can detect an open
   * VAR/timeout stoppage the same way the "Reanudar Tiempo Efectivo" button
   * already does. Defaults to false/false so every pre-existing caller
   * (tests, in particular) is unaffected.
   */
  isVarActive: boolean = false,
  isTimeoutActive: boolean = false,
  /**
   * Closes any open scoped stoppage (VAR and/or timeout). Unlike
   * handleModeSwitchGuarded (which REJECTS "Reanudar" outright while a
   * scoped stoppage is open), a referee-driven period transition cannot be
   * rejected the same way — the operator can't un-blow the whistle for
   * halftime/fulltime. So performTransition auto-closes the open stoppage(s)
   * first, then proceeds with the transition, instead of blocking it.
   */
  closeScopedStoppages: () => void | Promise<void> = () => {},
) => {
  const [currentPhase, setCurrentPhase] = useState<PeriodPhase>("NOT_STARTED");
  const [operatorPeriod, setOperatorPeriod] = useState(1);
  const [showExtraTimeAlert, setShowExtraTimeAlert] = useState(false);
  const [isTransitionPending, setIsTransitionPending] = useState(false);
  const lastMatchIdRef = useRef<string | null>(null);
  // Ref (not just the isTransitionPending state) so a second transition call
  // fired before the first render commit sees it is still blocked.
  const isTransitionPendingRef = useRef(false);

  // PMA-COCK-210: reuses the module-level `PHASE_RANK` export above —
  // was a locally re-declared, byte-identical copy of the same table.
  const phaseRank = PHASE_RANK;

  // Initialize phase from match status (monotonic: never regress the phase)
  useEffect(() => {
    if (!match?.status) return;

    const isResetMatch =
      match.status === "Pending" &&
      (match.match_time_seconds || 0) === 0 &&
      (match.ineffective_time_seconds || 0) === 0 &&
      !match.current_period_start_timestamp &&
      (!match.period_timestamps ||
        Object.keys(match.period_timestamps).length === 0);

    if (isResetMatch) {
      setCurrentPhase("NOT_STARTED");
      setOperatorPeriod(1);
      return;
    }

    const phaseFromStatus = statusToPhase(match.status);
    if (!phaseFromStatus) return;

    setCurrentPhase((prev) => {
      // If we are already in a later phase locally, don't regress unless explicit reset.
      if (phaseRank[phaseFromStatus] < phaseRank[prev]) return prev;
      return phaseFromStatus;
    });
  }, [
    match?.status,
    match?.match_time_seconds,
    match?.ineffective_time_seconds,
    match?.current_period_start_timestamp,
    match?.period_timestamps,
  ]);

  useEffect(() => {
    if (!match?.id) return;
    if (lastMatchIdRef.current && lastMatchIdRef.current !== match.id) {
      const nextPhase = statusToPhase(match.status) ?? "NOT_STARTED";
      setCurrentPhase(nextPhase);
      setShowExtraTimeAlert(false);
      if (nextPhase === "SECOND_HALF") setOperatorPeriod(2);
      else if (nextPhase === "FIRST_HALF_EXTRA_TIME") setOperatorPeriod(3);
      else if (nextPhase === "SECOND_HALF_EXTRA_TIME") setOperatorPeriod(4);
      else if (nextPhase === "PENALTIES") setOperatorPeriod(5);
      else setOperatorPeriod(1);
    }
    lastMatchIdRef.current = match.id;
  }, [match?.id, match?.status]);

  // Update operator period based on phase
  useEffect(() => {
    if (currentPhase === "SECOND_HALF") setOperatorPeriod(2);
    else if (currentPhase === "FIRST_HALF_EXTRA_TIME") setOperatorPeriod(3);
    else if (currentPhase === "SECOND_HALF_EXTRA_TIME") setOperatorPeriod(4);
    else if (currentPhase === "PENALTIES") setOperatorPeriod(5);
    else if (currentPhase === "FIRST_HALF") setOperatorPeriod(1);
    // Halftimes keep previous period usually, or next?
    // Standard: Halftime is technically end of P1, Extra Halftime end of P3.
    // Let's stick to simple mapping:
  }, [currentPhase]);

  // Calculate current period info based on effective time and current phase
  const getPeriodInfo = useCallback((): PeriodInfo => {
    let phase: PeriodPhase = currentPhase;
    let period = operatorPeriod;
    let isExtraTime =
      currentPhase === "FIRST_HALF_EXTRA_TIME" ||
      currentPhase === "EXTRA_HALFTIME" ||
      currentPhase === "SECOND_HALF_EXTRA_TIME";
    let extraTimeSeconds = 0;
    let shouldShowExtraTimeWarning = false;
    const elapsedSinceStart = computePeriodElapsedSeconds(
      currentPhase,
      globalTimeSeconds,
      match?.period_timestamps,
    );

    // Per-period minimum regulation seconds
    const periodMinimumSeconds =
      currentPhase === "FIRST_HALF_EXTRA_TIME" ||
      currentPhase === "SECOND_HALF_EXTRA_TIME"
        ? EXTRA_HALF_MINUTES * 60
        : REGULATION_FIRST_HALF_SECONDS;

    const canTransitionToHalftime = currentPhase === "FIRST_HALF";
    const canTransitionToSecondHalf = currentPhase === "HALFTIME";
    const canTransitionToFulltime = currentPhase === "SECOND_HALF"; // End of Regulation
    const canTransitionToExtraTimeFirst = currentPhase === "FULLTIME"; // From End of Regulation
    const canTransitionToExtraHalftime =
      currentPhase === "FIRST_HALF_EXTRA_TIME";
    const canTransitionToExtraTimeSecond = currentPhase === "EXTRA_HALFTIME";
    const canTransitionToPenalties = currentPhase === "SECOND_HALF_EXTRA_TIME";
    const canFinishMatch =
      currentPhase === "SECOND_HALF_EXTRA_TIME" || currentPhase === "PENALTIES";

    // Calculate extra time based on current phase and standard durations
    if (currentPhase === "FIRST_HALF") {
      period = 1;
      if (elapsedSinceStart >= REGULATION_FIRST_HALF_SECONDS) {
        extraTimeSeconds = elapsedSinceStart - REGULATION_FIRST_HALF_SECONDS;
        shouldShowExtraTimeWarning = true;
      }
    } else if (currentPhase === "SECOND_HALF") {
      period = 2;
      if (elapsedSinceStart >= REGULATION_FIRST_HALF_SECONDS) {
        extraTimeSeconds = elapsedSinceStart - REGULATION_FIRST_HALF_SECONDS;
        shouldShowExtraTimeWarning = true;
      }
    } else if (currentPhase === "FIRST_HALF_EXTRA_TIME") {
      period = 3;
      if (elapsedSinceStart >= EXTRA_HALF_MINUTES * 60) {
        extraTimeSeconds = elapsedSinceStart - EXTRA_HALF_MINUTES * 60;
        shouldShowExtraTimeWarning = true;
      }
    } else if (currentPhase === "SECOND_HALF_EXTRA_TIME") {
      period = 4;
      if (elapsedSinceStart >= EXTRA_HALF_MINUTES * 60) {
        extraTimeSeconds = elapsedSinceStart - EXTRA_HALF_MINUTES * 60;
        shouldShowExtraTimeWarning = true;
      }
    }

    return {
      period,
      phase,
      isExtraTime,
      extraTimeSeconds,
      shouldShowExtraTimeWarning,
      canTransitionToHalftime,
      canTransitionToSecondHalf,
      canTransitionToFulltime,
      canTransitionToExtraTimeFirst,
      canTransitionToExtraHalftime,
      canTransitionToExtraTimeSecond,
      canTransitionToPenalties,
      canFinishMatch,
      periodElapsedSeconds: elapsedSinceStart,
      periodMinimumSeconds,
    };
  }, [effectiveTime, currentPhase, operatorPeriod, globalTimeSeconds, match]);

  const periodInfo = getPeriodInfo();

  // Show extra time alert
  useEffect(() => {
    if (
      periodInfo.shouldShowExtraTimeWarning &&
      isClockRunning &&
      clockMode === "EFFECTIVE"
    ) {
      setShowExtraTimeAlert(true);
    }
  }, [periodInfo.shouldShowExtraTimeWarning, isClockRunning, clockMode]);

  // Transition Helpers
  const performTransition = async (
    targetPhase: PeriodPhase,
    targetStatus: Match["status"],
    targetMode: "EFFECTIVE" | "INEFFECTIVE" | null,
  ) => {
    if (!match) return false;
    if (isTransitionPendingRef.current) return false;
    console.log(`🔄 Transitioning to ${targetPhase}...`);

    const previousPhase = currentPhase;
    isTransitionPendingRef.current = true;
    setIsTransitionPending(true);
    setCurrentPhase(targetPhase);
    setShowExtraTimeAlert(false);

    try {
      // Send the current effective time so the backend doesn't miscalculate
      // match_time_seconds when clock_mode is INEFFECTIVE (it would otherwise
      // add the ineffective elapsed to the effective total).
      await updateMatchStatus(
        match.id,
        targetStatus,
        undefined,
        Math.round(effectiveTime),
      );
      console.log(`✅ Match status updated to ${targetStatus}`);

      if (targetMode === "EFFECTIVE") {
        // PMA-COCK-144 extension: this is the second door to the same
        // corruption the "Reanudar Tiempo Efectivo" guard closes — a period
        // transition that resumes EFFECTIVE (second half, either extra-time
        // half) with a VAR/timeout stoppage still open. Auto-close first
        // rather than reject: this transition is referee-driven and cannot
        // be un-clicked the way a resume button can.
        const clockState = deriveClockState({
          clockMode,
          isVarActive,
          isTimeoutActive,
        });
        if (clockState.phase === "STOPPED_SCOPED") {
          await closeScopedStoppages();
        }
      }

      if (targetMode) {
        await handleModeSwitch(targetMode);
      }

      fetchMatch();
      return true;
    } catch (error) {
      console.error(`Failed to transition to ${targetStatus}:`, error);
      // Roll back the optimistic phase so it never diverges from the
      // server-confirmed one — otherwise the monotonic re-sync guard above
      // (phaseRank comparison) permanently refuses to resync back down.
      setCurrentPhase(previousPhase);
      onTransitionError?.({ target: targetStatus as Match["status"], error });
      return false;
    } finally {
      isTransitionPendingRef.current = false;
      setIsTransitionPending(false);
    }
  };

  const transitionToHalftime = () =>
    performTransition("HALFTIME", "Halftime", null);
  const transitionToSecondHalf = () =>
    performTransition("SECOND_HALF", "Live_Second_Half", "EFFECTIVE");
  const transitionToFulltime = () =>
    performTransition("FULLTIME", "Fulltime", null);

  const transitionToExtraFirst = () =>
    performTransition("FIRST_HALF_EXTRA_TIME", "Live_Extra_First", "EFFECTIVE");
  const transitionToExtraHalftime = () =>
    performTransition("EXTRA_HALFTIME", "Extra_Halftime", null);
  const transitionToExtraSecond = () =>
    performTransition(
      "SECOND_HALF_EXTRA_TIME",
      "Live_Extra_Second",
      "EFFECTIVE",
    );
  const transitionToPenalties = () =>
    performTransition("PENALTIES", "Penalties", null);
  // Treat "match finished" as the standard Fulltime state to align with backend enums.
  const finishMatch = () => performTransition("COMPLETED", "Completed", null);

  const dismissExtraTimeAlert = useCallback(() => {
    setShowExtraTimeAlert(false);
  }, []);

  return {
    operatorPeriod,
    currentPhase,
    periodInfo,
    showExtraTimeAlert,
    isTransitionPending,
    transitionToHalftime,
    transitionToSecondHalf,
    transitionToFulltime,
    transitionToExtraFirst,
    transitionToExtraHalftime,
    transitionToExtraSecond,
    transitionToPenalties,
    finishMatch,
    dismissExtraTimeAlert,
  };
};
